// react
import { useCallback, useEffect, useMemo, useRef } from 'react';

// internationalization
import { useTranslation } from 'react-i18next';

// feature-hooks
import useEnergyGroupUnit from '@/features/energyxplorer/hooks/useEnergyGroupType';
import useResponsiveChartHeight from '@/features/energyxplorer/hooks/useResponsiveChartHeight';

// components
import { formatNumber } from '@/utils/formatters';

// relative
import {
  darkTheme,
  lightTheme,
} from '../../../../components/highcharts/themes';

import { useTheme } from '@emotion/react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import 'highcharts/highcharts-more';

const DurationAnalysisChart = ({
  data,
  xAxisMode = 'percentage',
  isLoading,
  querySkipped = false,
}) => {
  const { t } = useTranslation('energyxplorer-analysis-tabs');
  const chartHeight = useResponsiveChartHeight();
  const { effect } = useEnergyGroupUnit();

  const chartComponentRef = useRef(null);
  const theme = useTheme();

  const themeOptions = useMemo(
    () => (theme.palette.mode === 'dark' ? darkTheme : lightTheme),
    [theme.palette.mode],
  );

  // Imperative loading overlay
  useEffect(() => {
    const chart = chartComponentRef.current?.chart;
    if (!chart) return;
    if (isLoading && !querySkipped) {
      chart.showLoading(`${t('common.loading_data')}`);
      chart.hideNoData();
    } else chart.hideLoading();
  }, [isLoading, querySkipped, t]);

  const safeData = useMemo(
    () =>
      Array.isArray(data?.DurationAnalysisData)
        ? data.DurationAnalysisData
        : [],
    [data],
  );
  const safeProfiles = useMemo(
    () => (Array.isArray(data?.LoadProfiles) ? data.LoadProfiles : []),
    [data],
  );

  // Assumption: duration curve is typically sorted by Duration; if not, sort once.
  const sortedDurationData = (() => {
    if (!safeData.length) return [];

    for (let i = 1; i < safeData.length; i++) {
      if (safeData[i].Duration < safeData[i - 1].Duration) {
        return [...safeData].sort((a, b) => a.Duration - b.Duration);
      }
    }

    return safeData;
  })();

  // Memoized maxDuration
  const maxDuration = useMemo(() => {
    if (!sortedDurationData.length) return 0;
    return sortedDurationData[sortedDurationData.length - 1].Duration;
  }, [sortedDurationData]);

  // Main line series data
  const mainSeriesData = useMemo(() => {
    return sortedDurationData.map(({ Duration, Value }) => [Duration, Value]);
  }, [sortedDurationData]);

  // Build profile series efficiently by slicing (single pass per profile)
  const createLoadProfileSeries = useCallback(
    (profiles, durationData) => {
      const displayNameMap = {
        Peakload: t('duration_analysis.chart.legend.peak'),
        Intermediate: t('duration_analysis.chart.legend.intermediate'),
        Baseload: t('duration_analysis.chart.legend.base'),
      };

      const profileOrder = { Peakload: 0, Intermediate: 1, Baseload: 2 };

      const getProfileColors = (profileName) => {
        const rgb = {
          Peakload: [255, 0, 0],
          Intermediate: [255, 165, 0],
          Baseload: [0, 128, 0],
        }[profileName] || [0, 0, 255];

        const solid = `rgba(${rgb[0]}, ${rgb[1]}, ${rgb[2]}, 1)`;
        const gradient = {
          linearGradient: { x1: 0, y1: 0, x2: 0, y2: 1 },
          stops: [
            [0, `rgba(${rgb[0]}, ${rgb[1]}, ${rgb[2]}, 0.4)`],
            [1, `rgba(${rgb[0]}, ${rgb[1]}, ${rgb[2]}, 0.0)`],
          ],
        };
        return { solid, gradient };
      };

      // Helper: find first index with Duration >= value
      const lowerBound = (arr, value) => {
        let lo = 0;
        let hi = arr.length;
        while (lo < hi) {
          const mid = (lo + hi) >> 1;
          if (arr[mid].Duration < value) lo = mid + 1;
          else hi = mid;
        }
        return lo;
      };

      // Helper: find first index with Duration > value
      const upperBound = (arr, value) => {
        let lo = 0;
        let hi = arr.length;
        while (lo < hi) {
          const mid = (lo + hi) >> 1;
          if (arr[mid].Duration <= value) lo = mid + 1;
          else hi = mid;
        }
        return lo;
      };

      return [...profiles]
        .sort(
          (a, b) => (profileOrder[a.Name] ?? 99) - (profileOrder[b.Name] ?? 99),
        )
        .map((profile) => {
          const { Name, StartDuration, EndDuration } = profile;

          const startIdx = lowerBound(durationData, StartDuration);
          const endIdx = upperBound(durationData, EndDuration);
          const slice = durationData.slice(startIdx, endIdx);

          const seriesData = slice.map((p) => [p.Duration, p.Value]);

          const { solid, gradient } = getProfileColors(Name);

          return {
            name: displayNameMap[Name] || Name,
            data: seriesData,
            type: 'area',
            color: solid,
            fillColor: gradient,
            custom: {
              profileValue: profile.Value,
              shares: profile.Shares,
              sharesPercentage: profile.SharesPercentage,
              solidColor: solid,
            },
            showInLegend: true,
            enableMouseTracking: true,
          };
        });
    },
    [t],
  );

  const profileSeries = useMemo(() => {
    if (!safeProfiles.length || !sortedDurationData.length) return [];
    return createLoadProfileSeries(safeProfiles, sortedDurationData);
  }, [safeProfiles, sortedDurationData, createLoadProfileSeries]);

  // Axis label formatter and tooltip formatter: keep them stable via useCallback where feasible
  const xAxisLabelFormatter = useCallback(
    function () {
      if (xAxisMode === 'percentage' && maxDuration > 0) {
        const percentage = (this.value / maxDuration) * 100;
        return `${Math.round(percentage)}%`;
      }
      return this.value;
    },
    [xAxisMode, maxDuration],
  );

  const tooltipFormatter = useCallback(
    function () {
      let output = ``;

      (this.points || []).forEach((p) => {
        const custom = p.series?.userOptions?.custom;
        if (custom) {
          const color = custom.solidColor || p.color;
          const { profileValue, shares, sharesPercentage } = custom;

          output += `<span style="color:${color}">\u25CF</span> ${p.series.name}: <b>${formatNumber(
            profileValue,
          )} ${effect}</b><br/>`;
          output += `</b><br/>`;

          if (xAxisMode === 'percentage') {
            output += `<b>${t('duration_analysis.distribution_of')} ${p.series.name}</b><br/>
              ${t('duration_analysis.active_hours')}: <b>${formatNumber(
                (sharesPercentage?.ActivePeriod || 0) * 100,
              )}% </b><br/>
              ${t('duration_analysis.inactive_hours')}: <b>${formatNumber(
                (sharesPercentage?.InactivePeriod || 0) * 100,
              )}% </b><br/>`;
          } else {
            output += ` ${t('duration_analysis.active_hours')}: <b>${
              shares?.ActivePeriod ?? 0
            }</b><br/>
              ${t('duration_analysis.inactive_hours')}: <b>${
                shares?.InactivePeriod ?? 0
              }</b><br/>`;
          }
        } else {
          output += `<span style="color:${p.color}">\u25CF</span> ${p.series.name}: <b>${formatNumber(
            p.y,
          )} ${effect}</b><br/>`;
        }
      });

      return output;
    },
    [t, effect, xAxisMode],
  );

  // Final options (single memo) — no imperative series management, no theme update effect
  const chartOptions = useMemo(() => {
    const loadLabel = t('duration_analysis.chart.legend.load');

    return {
      lang: {
        noData: `${querySkipped ? `${t('common.invalid_filter_combination')}` : ''}`,
      },
      accessibility: {
        enabled: false,
      },
      noData: {
        style: {
          fontWeight: 'normal',
          color: theme.palette.mode === 'dark' ? 'lightgray' : 'gray',
        },
      },
      loading: {
        labelStyle: {
          fontWeight: 'normal',
          color: theme.palette.mode === 'dark' ? 'white' : 'black',
        },
        style: {
          backgroundColor: 'rgba(255, 255, 255, 0)',
        },
        showDuration: 150,
        hideDuration: 150,
      },
      boost: {
        enabled: false,
      },
      chart: {
        type: 'line',
        zoomType: 'x',
        zooming: { mouseWheel: false },
        height: chartHeight,
        backgroundColor: themeOptions.chart.backgroundColor,
        events: {
          load() {
            const chart = this;
            if (chart.__mouseleaveHandler) return;

            chart.__mouseleaveHandler = () => {
              // Tooltip
              chart.tooltip?.hide(0);

              // Clear hovered points (shared tooltip can use hoverPoints)
              if (chart.hoverPoints?.length) {
                chart.hoverPoints.forEach((p) => p.setState(''));
                chart.hoverPoints.length = 0;
              }

              // Clear single hovered point
              if (chart.hoverPoint) {
                chart.hoverPoint.setState('');
                chart.hoverPoint = null;
              }

              // Clear hovered series (removes “glow/thickening” highlight)
              if (chart.hoverSeries) {
                chart.hoverSeries.setState('');
                chart.hoverSeries = null;
              }

              // Clear series/point states (also removes inactive dimming)
              chart.series.forEach((s) => {
                s.setState('');
                // Points can keep state in some cases (markers/halos)
                if (s.points) s.points.forEach((p) => p.setState(''));
              });

              // Reset pointer + crosshairs
              chart.pointer?.reset?.(true);
              chart.xAxis?.forEach((a) => a.hideCrosshair?.());
              chart.yAxis?.forEach((a) => a.hideCrosshair?.());
            };

            chart.container.addEventListener(
              'mouseleave',
              chart.__mouseleaveHandler,
            );
          },
          destroy() {
            const chart = this;
            if (chart.__mouseleaveHandler) {
              chart.container.removeEventListener(
                'mouseleave',
                chart.__mouseleaveHandler,
              );
              chart.__mouseleaveHandler = null;
            }
          },
        },
      },
      title: { text: '' },
      credits: { enabled: false },
      time: { useUTC: true },

      xAxis: {
        title: {
          text:
            xAxisMode === 'percentage'
              ? t('duration_analysis.chart.x_axis_percentage')
              : t('duration_analysis.chart.x_axis_time'),
        },
        type: 'linear',
        min: 0,
        max: xAxisMode === 'percentage' ? maxDuration : null,
        tickInterval:
          xAxisMode === 'percentage' && maxDuration > 0
            ? maxDuration / 10
            : null,
        gridLineColor: 'transparent',
        crosshair: { width: 1, color: 'gray', dashStyle: 'shortdot' },
        lineColor: themeOptions.xAxis.lineColor,
        labels: {
          formatter: xAxisLabelFormatter,
          style: { color: themeOptions.xAxis.labels.style.color },
        },
      },

      yAxis: {
        title: { text: effect },
        gridLineColor: 'transparent',
        crosshair: { width: 1, color: 'gray', dashStyle: 'shortdot' },
        labels: { style: { color: themeOptions.yAxis.labels.style.color } },
      },

      legend: { itemStyle: { color: themeOptions.legend.itemStyle.color } },

      tooltip: {
        shared: true,
        useHTML: true,
        backgroundColor: themeOptions.tooltip.backgroundColor,
        borderColor: themeOptions.tooltip.borderColor,
        style: { color: theme.palette.mode === 'dark' ? 'white' : 'black' },
        formatter: tooltipFormatter,
      },

      plotOptions: {
        line: { marker: { enabled: false } },
        series: { animation: false }, // optional: reduces churn on frequent updates
      },

      series: [
        {
          name: loadLabel,
          data: mainSeriesData,
          type: 'line',
          color: theme.palette.mode === 'dark' ? 'gray' : 'lightgray',
        },
        ...profileSeries,
      ],
    };
  }, [
    t,
    querySkipped,
    theme.palette.mode,
    chartHeight,
    themeOptions.chart.backgroundColor,
    themeOptions.xAxis.lineColor,
    themeOptions.xAxis.labels.style.color,
    themeOptions.yAxis.labels.style.color,
    themeOptions.legend.itemStyle.color,
    themeOptions.tooltip.backgroundColor,
    themeOptions.tooltip.borderColor,
    xAxisMode,
    maxDuration,
    xAxisLabelFormatter,
    effect,
    tooltipFormatter,
    mainSeriesData,
    profileSeries,
  ]);

  return (
    <HighchartsReact
      highcharts={Highcharts}
      options={chartOptions}
      ref={chartComponentRef}
    />
  );
};

export default DurationAnalysisChart;
